
import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import ContentSkeleton from './ContentSkeleton.vue'
import ContentHeader from './Header.vue'
import ContentFooter from './Footer.vue'
import ContentComments from './Comments.vue'
import ContentInputBar from './InputBar.vue'
import ContentReport from './Report.vue'
import ErrorRight from './ErrorRight.vue'
import ContentVideo from './ContentVideo.vue'
import ContentSwiper from './ContentSwiper.vue'
import HeaderImage from '@xt/www/components/headerImage/index.vue'
import LazyImg from '@xt/www/components/lazyImg/index.vue'
import { EnumAuditBizType } from '@xt/client/enums'
import { checkToMemberHomePage } from '@xt/client/utils/routers-check/index'

import type { Options } from '@xt/client/entities/course-v2/user-content'

type ReplyMeta =
  | {
      type: 'review'
    }
  | {
      type: 'comment'
      rootCommentId?: string
      toCommentId?: string
      toMemberId?: string
    }

@Observer
@Component({
  components: {
    ContentSkeleton,
    LazyImg,
    ContentHeader,
    ContentFooter,
    ContentComments,
    ContentInputBar,
    ContentReport,
    HeaderImage,
    ErrorRight,
    ContentVideo,
    ContentSwiper
  }
})
export default class UserContent extends Vue {
  // 区分当前的内容主体来源 影响接口调用
  @Prop({ required: true }) options: Options

  @Prop({ required: true, default: true }) showBackButton: boolean

  /**
   * 当前弹窗的高度策略
   *
   * auto: 自动撑满屏幕高度 上下留出间距 缩小极限高度时整个弹窗滚动
   * fit: 高度父级元素会给出 完全按照父级弹窗的高度为准
   */
  @Prop({ required: false, default: 'auto' }) heightMode: 'auto' | 'fit'

  @Ref()
  inputBarRef: {
    updateLoading: (isLoading: boolean) => void
  }

  @Ref()
  swiper: {
    swiperInstance: {
      activeIndex
    }
  }

  // 当前swiper的索引
  activeIndex = 0

  footerBoxPlaceholder: string = ''

  showInputBar = false

  imageListSwiperOptions = {
    slidesPerView: 1,
    loop: false,
    autoplay: false,
    width: 712,
    pagination: {
      el: '.swiper-pagination'
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    }
  }

  replyMeta: null | ReplyMeta = null

  currentTab: 'review' | 'comment' | null = null

  showReport = false

  @Watch('options', { deep: true, immediate: true })
  async handleOptionsChange(option: Options | null) {
    if (option == null) {
      // 初始状态 跳过
      return
    }

    // 假数据状态不继续网络请求
    if (this.$store.$courseV2.GuideData.isGuide) {
      return
    }

    await this.Store.loadContent(option, this.UserStore.id)

    // 未登录时，不校验是否关注
    if (!this.Store.content || !this.UserStore.id) return

    if (this.Store.content.memberId !== this.UserStore.id) {
      this.Store.loadFocusStatus(this.Store.content.memberId)
    }
  }

  // 内容只有一张图片的情况下不展示底部指示点以及右上角的图片数量信息
  get isOneImage() {
    return this.Store.content && this.Store.content?.imageList.length === 1
  }

  get bizId() {
    if (!this.options) return null

    if (this.options.type === 'dairy') {
      return this.options.momentId
    } else {
      return this.options.homeworkId
    }
  }

  get bizType() {
    if (!this.options) return null

    if (this.options.type === 'dairy') {
      return EnumAuditBizType.Diary
    } else {
      return EnumAuditBizType.Homework
    }
  }

  // 独立的store 不依赖外部数据
  get Store() {
    return this.$store.$courseV2.UserContent
  }

  get UserStore() {
    return this.$store.$storeUser.UserInfo
  }

  get CommentsStore() {
    return this.Store.UserComments
  }

  async toProfile(id: string) {
    if (this.$route.name === 'my-center' && this.$route.query.memberId && this.$route.query.memberId === id) return

    const res = await checkToMemberHomePage(id)

    if (res) return

    window.open(`/my/center?memberId=${id}`, '_blank')
  }

  handleOpenReport() {
    if (!this.UserStore.id) {
      this.$emit('unauthorized')
      return
    }

    this.showReport = true
  }

  handleSlideChange() {
    if (this.swiper?.swiperInstance) {
      this.activeIndex = this.swiper.swiperInstance.activeIndex as number
    }
  }

  onImagePreview(index: number) {
    if (!this.Store.content) return

    this.$showViewer(
      this.Store.content.imageList.map(v => v.waterUrl),
      index
    )
  }

  handleSwitchTab(tab: 'comment' | 'review') {
    this.currentTab = tab

    if (this.showInputBar) {
      this.showInputBar = false
    }

    if (tab === 'comment') {
      this.footerBoxPlaceholder = '和桐学交流一下'
      this.replyMeta = { type: 'comment' }
    } else {
      this.footerBoxPlaceholder = this.CommentsStore.isHomeworkReviewed ? '回复助教' : '作业未评阅'
      this.replyMeta = { type: 'review' }
    }
  }

  async handleToggleVisibleRange() {
    if (!this.options) return

    // 公开私密功能：存在作业id，一律掉作业接口；不存在作业id，调日记接口 需求如此 【2025.6.19】
    const success = await this.Store.toggleVisibleRange()

    if (success) {
      this.$message.success('修改成功')

      // 如果当前位于日记瀑布流中 修改为私密状态后 应该操作瀑布流删除该数据
      if (this.options.type === 'dairy') {
        this.$emit('remove-success', { type: 'dairy', momentId: this.options.momentId })
      }
    }
  }

  async handleRemoveContent() {
    const success = await this.Store.removeContent()

    if (success) {
      this.$message.success('删除成功')

      if (this.options.type === 'homework') {
        this.$emit('remove-success', { type: 'homework', homeworkId: this.options.homeworkId })
      }

      if (this.options.type === 'dairy') {
        this.$emit('remove-success', { type: 'dairy', momentId: this.options.momentId })
      }
    }
  }

  handleClickBack() {
    this.$emit('click-back')
  }

  handleLikeSuccess() {
    // 用户点赞成功
    if (this.options.type === 'dairy') {
      this.$emit('like-success', { momentId: this.options.momentId })
    } else {
      this.$emit('like-success', { homeworkId: this.options.homeworkId })
    }
  }

  // 当输入框被用户关闭后 清除一些数据
  handleInputBarClosed() {
    if (!this.replyMeta) return

    if (this.replyMeta.type === 'comment') {
      this.footerBoxPlaceholder = '和桐学交流一下'
      this.replyMeta = { type: 'comment' }
    } else {
      this.footerBoxPlaceholder = this.CommentsStore.isHomeworkReviewed ? '回复助教' : '作业未评阅'
    }
  }

  handleReplyComment(data: { commentId: string; rootCommentId: string; toMemberId: string; toMemberNickname: string }) {
    this.footerBoxPlaceholder = `发送给：${data.toMemberNickname}`

    this.replyMeta = {
      type: 'comment',
      toCommentId: data.commentId,
      toMemberId: data.toMemberId,
      rootCommentId: data.rootCommentId
    }

    this.showInputBar = true
  }

  async handleSend({ value, stickers }: { value: string; stickers: string[] }) {
    if (!value && stickers.length === 0) {
      this.$message.error('评论内容不能为空')
      return
    }

    this.inputBarRef?.updateLoading(true)

    if (this.replyMeta?.type === 'review' && this.Store.fullOptions?.homeworkId) {
      const success = await this.CommentsStore.replyReview({
        content: value.trim(),
        stickers,
        homeworkId: this.Store.fullOptions.homeworkId
      })

      if (success) {
        this.$message.success('发送成功')
        this.showInputBar = false
      }
    } else if (this.replyMeta?.type === 'comment' && this.Store.fullOptions?.momentId) {
      const success = await this.CommentsStore.replyComment({
        content: value.trim(),
        stickers,
        toCommentId: this.replyMeta.toCommentId,
        toMemberId: this.replyMeta.toMemberId,
        rootCommentId: this.replyMeta.rootCommentId,
        momentId: this.Store.fullOptions.momentId
      })

      if (success) {
        this.$message.success('发送成功')
        this.showInputBar = false
      }
    }

    this.inputBarRef?.updateLoading(false)
  }

  handleShowInut() {
    if (this.Store.content?.isViolation) {
      this.$message.error('内容违规')
      return
    }

    this.showInputBar = true
  }

  async toggleFollow(memberId: string) {
    if (!this.UserStore.id) {
      this.$emit('unauthorized')
      return
    }

    const res = await this.Store.toggleFollow(memberId)

    if (res === true) {
      this.$emit('follow-success', this.Store.isFollow)
    }
  }

  created() {}
  mounted() {}
  updated() {}
  destroyed() {}
}
